import React, { useState, useEffect, useCallback } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { toast } from 'react-toastify';
import {
  getJournals,
  getJournalStats,
  approveJournal,
  rejectJournal,
  deleteJournal
} from '../services/journals.service';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    approved: { bg: 'bg-green-500/10', text: 'text-green-400', icon: 'check_circle', label: 'Approved' },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-400', icon: 'cancel', label: 'Rejected' },
    pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: 'schedule', label: 'Pending' }
  };

  const statusConfig = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      <span className="material-symbols-outlined text-sm">{statusConfig.icon}</span>
      {statusConfig.label}
    </span>
  );
};

// Rating Stars Component
const RatingStars = ({ rating }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`material-symbols-outlined text-lg ${star <= rating ? 'text-amber-400' : 'text-white/10'}`}
        >
          star
        </span>
      ))}
      <span className="ml-1 text-sm text-white/40">({rating})</span>
    </div>
  );
};

// View Journal Modal
const ViewModal = ({ isOpen, onClose, journal }) => {
  if (!isOpen || !journal) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-fade-in">
          
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Journal Details</h3>
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-6">
            {/* User Info */}
            <div className="flex items-center gap-4">
              {journal.user?.avatar_url ? (
                <img
                  src={journal.user.avatar_url}
                  alt={journal.user.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-medium">
                  {journal.user?.name?.charAt(0) || 'U'}
                </div>
              )}
              <div>
                <p className="font-medium text-white">{journal.user?.name || 'Unknown User'}</p>
                <p className="text-sm text-white/40">{journal.user?.email}</p>
              </div>
              <div className="ml-auto">
                <StatusBadge status={journal.status} />
              </div>
            </div>

            {/* Place */}
            {journal.place_name && (
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                <p className="text-sm text-white/40 mb-1">About Place</p>
                <div className="font-medium text-purple-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">location_on</span>
                  {journal.place_name}
                  {journal.state && <span className="text-white/40">• {journal.state}</span>}
                </div>
              </div>
            )}

            {/* Rating */}
            {journal.rating && (
              <div>
                <p className="text-sm text-white/40 mb-2">Rating</p>
                <RatingStars rating={journal.rating} />
              </div>
            )}

            {/* Title */}
            {journal.title && (
              <div>
                <p className="text-sm text-white/40 mb-1">Title</p>
                <p className="font-medium text-white">{journal.title}</p>
              </div>
            )}

            {/* Content */}
            <div>
              <p className="text-sm text-white/40 mb-2">Content</p>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                <p className="text-white/70 whitespace-pre-wrap">{journal.content}</p>
              </div>
            </div>

            {/* Images */}
            {journal.images && journal.images.length > 0 && (
              <div>
                <p className="text-sm text-white/40 mb-2">Images</p>
                <div className="grid grid-cols-2 gap-2">
                  {journal.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Journal ${index + 1}`}
                      className="rounded-xl max-h-40 object-cover w-full"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Votes */}
            {(journal.upvotes !== undefined || journal.downvotes !== undefined) && (
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-green-400">
                  <span className="material-symbols-outlined">thumb_up</span>
                  <span>{journal.upvotes || 0} upvotes</span>
                </div>
                <div className="flex items-center gap-2 text-red-400">
                  <span className="material-symbols-outlined">thumb_down</span>
                  <span>{journal.downvotes || 0} downvotes</span>
                </div>
              </div>
            )}

            {/* Date */}
            <p className="text-sm text-white/30">
              Created: {new Date(journal.created_at).toLocaleString('en-IN')}
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/5 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reject Confirmation Modal
const RejectModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-amber-400">cancel</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Reject Journal</h3>
            <p className="text-white/50">
              Are you sure you want to reject this journal? This will permanently delete it from the database.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Rejecting...
                </>
              ) : (
                'Reject'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-red-400">warning</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Delete Journal</h3>
            <p className="text-white/50">
              Are you sure you want to delete this journal? This action cannot be undone.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Journals() {
  const [journals, setJournals] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = usePersistedState('journals_status', '');
  const [viewModal, setViewModal] = useState({ isOpen: false, journal: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, journalId: null });
  const [rejectModal, setRejectModal] = useState({ isOpen: false, journalId: null });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchJournals = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (statusFilter) params.status = statusFilter;

      const response = await getJournals(params);
      setJournals(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching journals:', error);
      toast.error('Failed to load journals');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getJournalStats();
      setStats(response.data || response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleApprove = async (id) => {
    try {
      setActionLoading(id);
      await approveJournal(id);
      toast.success('Journal approved! It will now be visible on the main website.');
      fetchJournals();
      fetchStats();
    } catch (error) {
      console.error('Error approving journal:', error);
      toast.error('Failed to approve journal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.journalId) return;
    
    try {
      setActionLoading(rejectModal.journalId);
      await rejectJournal(rejectModal.journalId);
      toast.success('Journal rejected and deleted from database.');
      setRejectModal({ isOpen: false, journalId: null });
      fetchJournals();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting journal:', error);
      toast.error('Failed to reject journal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.journalId) return;

    try {
      setActionLoading(deleteModal.journalId);
      await deleteJournal(deleteModal.journalId);
      toast.success('Journal deleted');
      setDeleteModal({ isOpen: false, journalId: null });
      fetchJournals();
      fetchStats();
    } catch (error) {
      console.error('Error deleting journal:', error);
      toast.error('Failed to delete journal');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };

  if (isLoading && journals.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      
      {/* ===== HEADER ===== */}
      <div>
        <h1 className="text-3xl font-extralight tracking-tight text-white">
          Journals<span className="text-purple-400 font-bold">.</span>
        </h1>
        <p className="text-sm text-white/40 mt-1">Review and moderate user travel journals</p>
      </div>

      {/* ===== STATS ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total || 0, icon: 'auto_stories', color: 'purple' },
          { label: 'Pending', value: stats.pending || 0, icon: 'schedule', color: 'amber' },
          { label: 'Approved', value: stats.approved || 0, icon: 'check_circle', color: 'green' },
          { label: 'Rejected', value: stats.rejected || 0, icon: 'cancel', color: 'red' },
        ].map((stat) => (
          <div 
            key={stat.label} 
            className={`bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-${stat.color}-500/30 transition-all duration-300`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-500/10 flex items-center justify-center`}>
                <span className={`material-symbols-outlined text-2xl text-${stat.color}-400`}>{stat.icon}</span>
              </div>
              <div>
                <p className="text-2xl font-light text-white">{stat.value}</p>
                <p className="text-xs text-white/40">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== FILTERS ===== */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50 transition-all"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-xl font-medium transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">close</span>
            Clear
          </button>
        )}
      </div>

      {/* ===== JOURNAL BLOCKS GRID ===== */}
      <div className="space-y-4">
        <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider">
          Journals ({journals.length}{pagination.total > 0 ? ` of ${pagination.total}` : ''})
        </h2>
        {journals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {journals.map((journal) => (
              <div
                key={journal.id}
                className="group bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-white/10 hover:bg-white/[0.04] transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {journal.user?.avatar_url ? (
                      <img
                        src={journal.user.avatar_url}
                        alt={journal.user.name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                        {journal.user?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate">{journal.user?.name || 'Unknown User'}</p>
                      <StatusBadge status={journal.status} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setViewModal({ isOpen: true, journal })}
                      className="p-2 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                      title="View"
                    >
                      <span className="material-symbols-outlined text-lg">visibility</span>
                    </button>
                    {journal.status !== 'approved' && (
                      <button
                        onClick={() => handleApprove(journal.id)}
                        disabled={actionLoading === journal.id}
                        className="p-2 text-white/40 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Approve"
                      >
                        {actionLoading === journal.id ? (
                          <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                        ) : (
                          <span className="material-symbols-outlined text-lg">check_circle</span>
                        )}
                      </button>
                    )}
                    {journal.status !== 'rejected' && (
                      <button
                        onClick={() => setRejectModal({ isOpen: true, journalId: journal.id })}
                        disabled={actionLoading === journal.id}
                        className="p-2 text-white/40 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Reject"
                      >
                        <span className="material-symbols-outlined text-lg">cancel</span>
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteModal({ isOpen: true, journalId: journal.id })}
                      disabled={actionLoading === journal.id}
                      className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>
                </div>
                {journal.place_name && (
                  <div className="text-sm text-purple-400 flex items-center gap-1 mb-2">
                    <span className="material-symbols-outlined text-sm">location_on</span>
                    <span className="truncate">{journal.place_name}{journal.state ? ` • ${journal.state}` : ''}</span>
                  </div>
                )}
                {journal.title && (
                  <p className="font-medium text-white/80 mb-1 line-clamp-1">{journal.title}</p>
                )}
                <p className="text-sm text-white/40 line-clamp-2 mb-3">
                  {truncateContent(journal.content, 120)}
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                  {journal.rating ? <RatingStars rating={journal.rating} /> : null}
                  <span className="text-xs text-white/30">{formatDate(journal.created_at)}</span>
                  {journal.total_votes !== undefined && journal.total_votes !== 0 && (
                    <span className="text-xs text-white/30">{journal.total_votes > 0 ? '+' : ''}{journal.total_votes} votes</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-white/20">auto_stories</span>
            </div>
            <p className="font-medium text-white mb-1">No journals found</p>
            <p className="text-white/40 text-sm">
              {statusFilter ? 'Try changing the filter' : 'No journals to moderate yet'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-white/40">
              Showing {journals.length} of {pagination.total} journals
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-white/40">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ViewModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, journal: null })}
        journal={viewModal.journal}
      />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, journalId: null })}
        onConfirm={handleDelete}
        isLoading={actionLoading === deleteModal.journalId}
      />

      <RejectModal
        isOpen={rejectModal.isOpen}
        onClose={() => setRejectModal({ isOpen: false, journalId: null })}
        onConfirm={handleReject}
        isLoading={actionLoading === rejectModal.journalId}
      />
    </div>
  );
}

export default Journals;
